/**
 * @fileoverview Centralized branding defaults - Single source of truth
 *
 * Architecture follows white-label SaaS best practices:
 * - JSON-serializable for versioning and rollback
 * - Tenant-overridable at every level
 * - Accessibility-compliant color contrasts (WCAG 2.1 AA)
 * - Dark mode support built-in
 * - Responsive breakpoint tokens
 *
 * @see https://www.thisdot.co/blog/layouts-and-theming-in-vuetify-3
 */

// ═══════════════════════════════════════════════════════════════════════════
// COMPANY IDENTITY
// ═══════════════════════════════════════════════════════════════════════════

export const IDENTITY_DEFAULTS = {
  companyName: 'Planthra',
  legalEntity: 'Planthra Inc.',
  domain: 'planthra.com',
  tagline: 'Multi-tenant SaaS platform for growing businesses.',
  description: 'Build, launch, and scale your SaaS with enterprise features.',
  copyrightHolder: 'Planthra',
  supportEmail: 'support@planthra.com',
  salesEmail: 'sales@planthra.com',
  noReplyEmail: 'noreply@planthra.com',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// COLOR SYSTEM (WCAG 2.1 AA Compliant - 4.5:1 minimum contrast)
// ═══════════════════════════════════════════════════════════════════════════

export const COLOR_DEFAULTS = {
  // Brand Colors
  primary: '#78DC78',
  primaryDarken1: '#5CB85C',
  primaryDarken2: '#4A9D4A',
  primaryLighten1: '#A8E6A8',
  secondary: '#00B4B4',
  secondaryDarken1: '#009090',
  secondaryLighten1: '#33C9C9',
  accent: '#FFCC00',

  // Semantic Colors (light mode)
  info: '#285AFF',
  success: '#388E3C',
  warning: '#FFA000',
  error: '#D32F2F',

  // Surface Colors (light mode)
  background: '#FAFAFA',
  surface: '#FFFFFF',
  surfaceBright: '#FFFFFF',
  surfaceLight: '#F5F5F5',
  surfaceVariant: '#E0E0E0',

  // Text Colors
  onPrimary: '#FFFFFF',
  onSecondary: '#FFFFFF',
  onBackground: '#212121',
  onSurface: '#212121',
  onSurfaceVariant: '#424242',
  textSecondary: '#595959', // WCAG AA compliant: 5.9:1 contrast on white

  // Footer
  footerBackground: '#212121',
  footerOnBackground: '#FFFFFF',
} as const

export const COLOR_DARK_OVERRIDES = {
  error: '#EF5350',
  warning: '#FFB74D',
  success: '#66BB6A',
  info: '#42A5F5',
  background: '#121212',
  surface: '#1E1E1E',
  surfaceBright: '#2A2A2A',
  surfaceLight: '#2A2A2A',
  surfaceVariant: '#424242',
  onBackground: '#FFFFFF',
  onSurface: '#FFFFFF',
  onSurfaceVariant: '#BDBDBD',
} as const

export const ADMIN_COLOR_DEFAULTS = {
  background: '#121212',
  surface: '#1E1E1E',
  surfaceBright: '#2A2A2A',
  appBar: '#2A2A2A',
  onSurface: '#FFFFFF',
  onBackground: '#FFFFFF',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

export const FONT_DEFAULTS = {
  heading: 'Inter',
  body: 'Inter',
  mono: 'JetBrains Mono',
  fallback: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
} as const

export const FONT_SIZE_DEFAULTS = {
  h1: '2.5rem',    // 40px
  h2: '2rem',      // 32px
  h3: '1.75rem',   // 28px
  h4: '1.5rem',    // 24px
  h5: '1.25rem',   // 20px
  h6: '1rem',      // 16px
  body1: '1rem',   // 16px
  body2: '0.875rem', // 14px
  caption: '0.75rem', // 12px
  overline: '0.625rem', // 10px
} as const

export const FONT_WEIGHT_DEFAULTS = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const

// ═══════════════════════════════════════════════════════════════════════════
// SPACING SYSTEM (4px base unit)
// ═══════════════════════════════════════════════════════════════════════════

export const SPACING_DEFAULTS = {
  unit: 4,
  xs: '4px',    // 1 unit
  sm: '8px',    // 2 units
  md: '16px',   // 4 units
  lg: '24px',   // 6 units
  xl: '32px',   // 8 units
  xxl: '48px',  // 12 units
} as const

// ═══════════════════════════════════════════════════════════════════════════
// BORDER SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

export const BORDER_DEFAULTS = {
  radiusNone: '0',
  radiusSm: '4px',
  radiusMd: '8px',
  radiusLg: '12px',
  radiusXl: '16px',
  radiusPill: '9999px',
  widthThin: '1px',
  widthMedium: '2px',
  widthThick: '4px',
  colorDefault: '#E0E0E0',
  colorFocus: 'currentColor',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// ELEVATION/SHADOW SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

export const ELEVATION_DEFAULTS = {
  none: 'none',
  xs: '0 1px 2px #F5F5F5',
  sm: '0 1px 3px #EEEEEE',
  md: '0 4px 6px #E0E0E0',
  lg: '0 10px 15px #D0D0D0',
  xl: '0 20px 25px #C0C0C0',
  xxl: '0 25px 50px #A0A0A0',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// TRANSITION/ANIMATION SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

export const TRANSITION_DEFAULTS = {
  durationFast: '150ms',
  durationNormal: '250ms',
  durationSlow: '400ms',
  easingStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easingDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
  easingAccelerate: 'cubic-bezier(0.4, 0, 1, 1)',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// Z-INDEX SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

export const Z_INDEX_DEFAULTS = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  snackbar: 1080,
  loading: 9999,
} as const

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSIVE BREAKPOINTS
// ═══════════════════════════════════════════════════════════════════════════

export const BREAKPOINT_DEFAULTS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
} as const

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT DEFAULTS
// ═══════════════════════════════════════════════════════════════════════════

export const COMPONENT_DEFAULTS = {
  card: {
    rounded: 'lg',
    elevation: 2,
  },
  button: {
    rounded: 'lg',
    density: 'comfortable' as const,
  },
  input: {
    variant: 'outlined' as const,
    density: 'comfortable' as const,
  },
  list: {
    density: 'compact' as const,
  },
} as const

// ═══════════════════════════════════════════════════════════════════════════
// LOGO SIZING (Auth Pages)
// Uses CSS clamp(min, preferred, max) for responsive sizing per breakpoint.
// - min: Minimum size in pixels (floor)
// - preferred: Viewport-based size in vh units (fluid scaling)
// - max: Maximum size in pixels (ceiling)
// Configurable via Developer Settings > Branding > Settings tab
// ═══════════════════════════════════════════════════════════════════════════

export const LOGO_SIZE_DEFAULTS = {
  mobile: {
    min: 80,      // Minimum size in px
    preferred: 12, // Preferred size in vh
    max: 160,     // Maximum size in px
  },
  tablet: {
    min: 96,
    preferred: 15,
    max: 192,
  },
  desktop: {
    min: 128,
    preferred: 18,
    max: 256,
  },
} as const

// Use a flexible interface instead of typeof to allow any number values
export interface LogoSizeBreakpoint {
  min: number
  preferred: number
  max: number
}

export interface LogoSizeConfig {
  mobile: LogoSizeBreakpoint
  tablet: LogoSizeBreakpoint
  desktop: LogoSizeBreakpoint
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN PANEL
// ═══════════════════════════════════════════════════════════════════════════

export const ADMIN_DEFAULTS = {
  title: 'Platform Admin',
  subtitle: 'Super Admin Mode',
  theme: 'dark' as const,
} as const

// ═══════════════════════════════════════════════════════════════════════════
// EXTERNAL BRAND COLORS (Third-party platforms)
// These are official brand colors that cannot be tokenized.
// ═══════════════════════════════════════════════════════════════════════════

export const EXTERNAL_BRAND_COLORS = {
  // Social Media
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  facebook: '#1877F2',

  // Review Platforms
  g2: '#FF492C',
  capterra: '#FF9D28',
  google: '#4285F4',
  trustpilot: '#00B67A',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// SOCIAL LINKS
// ═══════════════════════════════════════════════════════════════════════════

export const SOCIAL_DEFAULTS = {
  twitter: '',
  linkedin: '',
  github: '',
  facebook: '',
  instagram: '',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// ASSET PATHS (fallbacks)
// ═══════════════════════════════════════════════════════════════════════════

export const PATH_DEFAULTS = {
  logoLight: '/img/planthra-logo.svg',
  logoDark: '/img/planthra-logo-dark.svg',
  icon: '/img/planthra-icon.svg',
  favicon: '/favicon.svg',
  placeholder: '/img/placeholder.svg',
  dashboardPreview: '/img/dashboard-preview.png',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// STORAGE KEYS
// ═══════════════════════════════════════════════════════════════════════════

export const STORAGE_DEFAULTS = {
  prefix: 'planthra',
  themeKey: 'theme',
  localeKey: 'locale',
} as const

// ═══════════════════════════════════════════════════════════════════════════
// COLOR PRESETS (for quick theming)
// ═══════════════════════════════════════════════════════════════════════════

export const COLOR_PRESETS = [
  { name: 'Planthra Green', primary: '#78DC78', secondary: '#00B4B4' },
  { name: 'Ocean Teal', primary: '#00B4B4', secondary: '#78DC78' },
  { name: 'Sunset Yellow', primary: '#FFCC00', secondary: '#78DC78' },
  { name: 'Royal Blue', primary: '#285AFF', secondary: '#00B4B4' },
  { name: 'Royal Purple', primary: '#9C27B0', secondary: '#E91E63' },
  { name: 'Corporate Indigo', primary: '#3F51B5', secondary: '#673AB7' },
] as const

// ═══════════════════════════════════════════════════════════════════════════
// ACCESSIBILITY
// ═══════════════════════════════════════════════════════════════════════════

export const ACCESSIBILITY_DEFAULTS = {
  minContrastRatio: 4.5, // WCAG AA
  focusRingWidth: '2px',
  focusRingColor: 'currentColor',
  focusRingOffset: '2px',
  minTouchTarget: '44px', // iOS/Android minimum
} as const

// ═══════════════════════════════════════════════════════════════════════════
// TIMING CONSTANTS (for consistency)
// ═══════════════════════════════════════════════════════════════════════════

export const TIMING_DEFAULTS = {
  debounce: 300,
  throttle: 100,
  toastDuration: 5000,
  toastErrorDuration: 8000,
  saveTimeout: 10000,
  refreshInterval: 30000,
} as const

// ═══════════════════════════════════════════════════════════════════════════
// UNIFIED BRANDING DEFAULTS OBJECT
// ═══════════════════════════════════════════════════════════════════════════

export const BRANDING_DEFAULTS = {
  identity: IDENTITY_DEFAULTS,
  colors: COLOR_DEFAULTS,
  colorsDark: COLOR_DARK_OVERRIDES,
  adminColors: ADMIN_COLOR_DEFAULTS,
  externalBrandColors: EXTERNAL_BRAND_COLORS,
  fonts: FONT_DEFAULTS,
  fontSizes: FONT_SIZE_DEFAULTS,
  fontWeights: FONT_WEIGHT_DEFAULTS,
  spacing: SPACING_DEFAULTS,
  borders: BORDER_DEFAULTS,
  elevation: ELEVATION_DEFAULTS,
  transitions: TRANSITION_DEFAULTS,
  zIndex: Z_INDEX_DEFAULTS,
  breakpoints: BREAKPOINT_DEFAULTS,
  components: COMPONENT_DEFAULTS,
  logoSizes: LOGO_SIZE_DEFAULTS as LogoSizeConfig,
  admin: ADMIN_DEFAULTS,
  social: SOCIAL_DEFAULTS,
  paths: PATH_DEFAULTS,
  storage: STORAGE_DEFAULTS,
  presets: COLOR_PRESETS,
  accessibility: ACCESSIBILITY_DEFAULTS,
  timing: TIMING_DEFAULTS,
} as const

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type BrandingDefaults = typeof BRANDING_DEFAULTS
export type IdentityConfig = typeof IDENTITY_DEFAULTS
export type ColorConfig = typeof COLOR_DEFAULTS
export type ColorDarkConfig = typeof COLOR_DARK_OVERRIDES
export type AdminColorConfig = typeof ADMIN_COLOR_DEFAULTS
export type ExternalBrandColorsConfig = typeof EXTERNAL_BRAND_COLORS
export type FontConfig = typeof FONT_DEFAULTS
export type FontSizeConfig = typeof FONT_SIZE_DEFAULTS
export type FontWeightConfig = typeof FONT_WEIGHT_DEFAULTS
export type SpacingConfig = typeof SPACING_DEFAULTS
export type BorderConfig = typeof BORDER_DEFAULTS
export type ElevationConfig = typeof ELEVATION_DEFAULTS
export type TransitionConfig = typeof TRANSITION_DEFAULTS
export type ZIndexConfig = typeof Z_INDEX_DEFAULTS
export type BreakpointConfig = typeof BREAKPOINT_DEFAULTS
export type ComponentConfig = typeof COMPONENT_DEFAULTS
export type AdminConfig = typeof ADMIN_DEFAULTS
export type SocialConfig = typeof SOCIAL_DEFAULTS
export type PathConfig = typeof PATH_DEFAULTS
export type StorageConfig = typeof STORAGE_DEFAULTS
export type ColorPreset = (typeof COLOR_PRESETS)[number]
export type AccessibilityConfig = typeof ACCESSIBILITY_DEFAULTS
export type TimingConfig = typeof TIMING_DEFAULTS

export default BRANDING_DEFAULTS
